import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, Input, ScreenHeader
} from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAReportRecipients() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  const [ reportRecipients, setReportRecipients ] = useState(
    powerOfAttorney.report_recipients || [ {
      name: '',
      address: '',
      phone: '',
      alt_phone: '',
      email: ''
    } ]
  );
  
  useScrollToTop();
  
  const handleBack = useCallback(() => {
    navigate('../period-reports');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    const validRecipients = reportRecipients.filter(recipient => 
      recipient.name.trim() !== '' && recipient.address.trim() !== ''
    );
    setPowerOfAttorney(prev => ({ 
      ...prev, 
      report_recipients: validRecipients
    }));
    navigate('../report-frequency');
  }, [ reportRecipients, setPowerOfAttorney, navigate ]);

  const addRecipient = useCallback(() => {
    setReportRecipients(prev => [ ...prev, {
      name: '',
      address: '',
      phone: '',
      alt_phone: '',
      email: ''
    } ]);
  }, []);

  const removeRecipient = useCallback((index: number) => {
    setReportRecipients(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateRecipient = useCallback((index: number, field: string, value: string) => {
    setReportRecipients(prev => prev.map((recipient, i) => 
      i === index ? { ...recipient, [field]: value } : recipient
    ));
  }, []);

  const isFormValid = reportRecipients.some(recipient => 
    recipient.name.trim() !== '' && recipient.address.trim() !== ''
  );
  
  return (
    <div className="new-poa-report-recipients screen-container screen">
      <ScreenHeader
        title="Who Should Receive Reports"
        description="Specify who should receive regular reports from your agent"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>Report Recipients</h3>
        <p>
          List the people who should receive copies of the period reports 
          from your agent. This might include family members, attorneys, 
          or other trusted individuals.
        </p>

        <div className="recipients-list">
          {reportRecipients.map((recipient, index) => (
            <div key={index} className="recipient-item">
              <div className="recipient-header">
                {reportRecipients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRecipient(index)}
                    className="remove-button"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="recipient-form">
                <div className="form-group">
                  <Input
                    id={`recipient-name-${index}`}
                    type="text"
                    value={recipient.name}
                    label="Full Name"
                    required
                    onChange={(e) => updateRecipient(index, 'name', (e.target as HTMLInputElement).value)}
                    className="form-input"
                    placeholder="Full name"
                  />
                </div>

                <div className="form-group">
                  <Input
                    id={`recipient-address-${index}`}
                    type="text"
                    value={recipient.address}
                    label="Mailing Address"
                    required
                    onChange={(e) => updateRecipient(index, 'address', (e.target as HTMLInputElement).value)}
                    className="form-input"
                    placeholder="Street address, city, state, zip"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <Input
                      id={`recipient-phone-${index}`}
                      type="tel"
                      value={recipient.phone}
                      label="Phone Number"
                      required
                      onChange={(e) => updateRecipient(index, 'phone', (e.target as HTMLInputElement).value)}
                      className="form-input"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      id={`recipient-alt-phone-${index}`}
                      type="tel"
                      value={recipient.alt_phone}
                      label="Alternate Phone"
                      onChange={(e) => updateRecipient(index, 'alt_phone', (e.target as HTMLInputElement).value)}
                      className="form-input"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Input
                    id={`recipient-email-${index}`}
                    type="email"
                    value={recipient.email}
                    label="Email Address (Optional)"
                    onChange={(e) => updateRecipient(index, 'email', (e.target as HTMLInputElement).value)}
                    className="form-input"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addRecipient}
            className="add-recipient-button"
          >
            + Add Another Person
          </button>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
      />
    </div>
  );
}
